import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

function Booking() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();

  const workerFromState = location.state?.worker || null;
  const workerIdFromQuery = searchParams.get("workerId");

  const [worker, setWorker] = useState(workerFromState);
  const [loadingWorker, setLoadingWorker] = useState(!workerFromState && !!workerIdFromQuery);

  const [formData, setFormData] = useState({
    customerName: "",
    phone: "",
    flat_house_no: "",
    street_area: "",
    city: "Hyderabad",
    state: "Telangana",
    country: "India",
    pincode: "",
  });

  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (profile) {
      setFormData((prev) => ({
        ...prev,
        customerName: profile.full_name || "",
        phone: profile.phone || "",
        flat_house_no: profile.flat_house_no || "",
        street_area: profile.street_area || "",
        city: profile.city || "Hyderabad",
        state: profile.state || "Telangana",
        country: profile.country || "India",
        pincode: profile.pincode || "",
      }));
    }
  }, [profile]);

  useEffect(() => {
    const fetchWorker = async () => {
      if (workerFromState || !workerIdFromQuery) return;

      try {
        setLoadingWorker(true);

        const { data, error } = await supabase
          .from("workers")
          .select(`
            *,
            profiles (
              id,
              full_name,
              phone,
              city
            )
          `)
          .eq("id", workerIdFromQuery)
          .maybeSingle();

        if (error) throw error;

        if (!data) {
          setErrorMessage("Worker not found.");
          return;
        }

        setWorker({
          id: data.id,
          name: data.profiles?.full_name || "Worker",
          rating: Number(data.average_rating || 0).toFixed(1),
          price: "Price at checkout",
          service: data.nature_of_work || data.skill || "General Service",
          experience: data.skills || "Not specified",
          location: data.online_city || data.profiles?.city || "N/A",
          availability: data.online_status || "offline",
          workerRecord: data,
        });
      } catch (error) {
        console.error("Fetch worker error:", error);
        setErrorMessage(error.message || "Failed to load worker.");
      } finally {
        setLoadingWorker(false);
      }
    };

    fetchWorker();
  }, [workerFromState, workerIdFromQuery]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const formattedAddress = useMemo(() => {
    return [
      formData.flat_house_no,
      formData.street_area,
      formData.city,
      formData.state,
      formData.country,
      formData.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  }, [formData]);

  const handleConfirm = async (e) => {
    e.preventDefault();

    if (!user?.id) {
      setErrorMessage("Please sign up or log in before placing a booking.");
      navigate("/login");
      return;
    }

    if (!worker) {
      setErrorMessage("No worker selected.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const workerId =
        worker.workerRecord?.id || worker.id || null;

      const bookingPayload = {
        customer_profile_id: user.id,
        worker_id: workerId,
        service_type: worker.service || "general_service",
        booking_date: new Date().toISOString().split("T")[0],
        booking_slot: null,
        address_text: formattedAddress,
        flat_house_no: formData.flat_house_no,
        street_area: formData.street_area,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        pincode: formData.pincode,
        customer_phone: formData.phone,
        status: "pending",
        price: 0,
        notes: `Worker: ${worker.name} | Customer: ${formData.customerName} | Phone: ${formData.phone}`,
      };

      const { data, error } = await supabase
        .from("bookings")
        .insert([bookingPayload])
        .select();

      if (error) throw error;

      const existingBookings =
        JSON.parse(localStorage.getItem("mistribazaarBookings")) || [];

      const newBooking = {
        id: data?.[0]?.id || Date.now(),
        workerName: worker.name,
        service: worker.service,
        price: worker.price,
        customerName: formData.customerName,
        phone: formData.phone,
        address: formattedAddress,
        status: "pending",
        createdAt: new Date().toISOString(),
      };

      localStorage.setItem(
        "mistribazaarBookings",
        JSON.stringify([...existingBookings, newBooking])
      );

      setConfirmed(true);
    } catch (error) {
      console.error("BOOKING ERROR:", error);
      setErrorMessage(error.message || "Failed to confirm booking.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingWorker) {
    return (
      <div style={page}>
        <div style={card}>
          <h1>Loading worker...</h1>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div style={page}>
        <div style={card}>
          <h1>No worker selected</h1>
          <button onClick={() => navigate("/")} style={solidBtn}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <button onClick={() => navigate(-1)} style={backBtn}>
        Back
      </button>

      <div style={heroCard}>
        <h1 style={{ marginTop: 0 }}>Book {worker.name}</h1>
        <p style={muted}>{worker.service}</p>
      </div>

      <div style={summaryCard}>
        <p><strong>Worker:</strong> {worker.name}</p>
        <p><strong>Rating:</strong> {worker.rating} ⭐</p>
        <p><strong>Location:</strong> {worker.location}</p>
        <p><strong>Status:</strong> {worker.availability}</p>
      </div>

      {errorMessage && (
        <div style={errorBox}>
          {errorMessage}
        </div>
      )}

      {!confirmed ? (
        <form onSubmit={handleConfirm} style={card}>
          <input
            type="text"
            name="customerName"
            placeholder="Your Name"
            value={formData.customerName}
            onChange={handleChange}
            required
            style={input}
          />

          <input
            type="text"
            name="phone"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleChange}
            required
            style={input}
          />

          <input
            type="text"
            name="flat_house_no"
            placeholder="Flat No / House No"
            value={formData.flat_house_no}
            onChange={handleChange}
            required
            style={input}
          />

          <input
            type="text"
            name="street_area"
            placeholder="Street / Area"
            value={formData.street_area}
            onChange={handleChange}
            required
            style={input}
          />

          <div style={grid2}>
            <input
              type="text"
              name="city"
              placeholder="City"
              value={formData.city}
              onChange={handleChange}
              required
              style={input}
            />

            <input
              type="text"
              name="state"
              placeholder="State"
              value={formData.state}
              onChange={handleChange}
              required
              style={input}
            />

            <input
              type="text"
              name="country"
              placeholder="Country"
              value={formData.country}
              onChange={handleChange}
              required
              style={input}
            />

            <input
              type="text"
              name="pincode"
              placeholder="Pincode"
              value={formData.pincode}
              onChange={handleChange}
              required
              style={input}
            />
          </div>

          <div style={previewBox}>
            <strong>Address Preview:</strong>
            <div style={{ marginTop: "8px", color: "#d1d1d1" }}>
              {formattedAddress}
            </div>
          </div>

          <button type="submit" disabled={loading} style={solidBtn}>
            {loading ? "Confirming..." : "Confirm Booking"}
          </button>
        </form>
      ) : (
        <div style={successBox}>
          <h2>Booking Confirmed</h2>
          <p><strong>Name:</strong> {formData.customerName}</p>
          <p><strong>Phone:</strong> {formData.phone}</p>
          <p><strong>Address:</strong> {formattedAddress}</p>
          <p>Your request has been sent successfully.</p>

          <button onClick={() => navigate("/my-bookings")} style={solidBtn}>
            View My Bookings
          </button>
        </div>
      )}
    </div>
  );
}

const page = {
  padding: "40px",
  fontFamily: "Arial, sans-serif",
  maxWidth: "800px",
  margin: "0 auto",
  background: "#000",
  color: "#fff",
  minHeight: "100vh",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "24px",
  marginBottom: "18px",
};

const summaryCard = {
  background: "#111",
  border: "1px solid #222",
  borderRadius: "16px",
  padding: "20px",
  marginBottom: "18px",
};

const card = {
  background: "#111",
  border: "1px solid #222",
  borderRadius: "16px",
  padding: "20px",
};

const successBox = {
  background: "#0f2416",
  border: "1px solid #1f5d34",
  borderRadius: "16px",
  padding: "20px",
};

const errorBox = {
  background: "#2a1111",
  color: "#ff9a9a",
  padding: "12px 16px",
  borderRadius: "10px",
  marginBottom: "16px",
};

const previewBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "12px",
  padding: "14px",
  marginBottom: "14px",
};

const input = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "12px",
};

const backBtn = {
  marginBottom: "20px",
  padding: "10px 16px",
  border: "1px solid #444",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const solidBtn = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const muted = {
  color: "#d1d1d1",
  marginBottom: 0,
};

export default Booking;